import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";
import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";
import {
  buildChildrensBookPhysicalPageItems,
  childrensBookTotalPhysicalPages,
} from "../utils/childrensBookPhysicalPagination";

import {
  childrensBookAuthoringSurfaceBoxStyle,
} from "./ChildrensBookAuthoringImageSurface";

import {
  childrensBookAuthoringPageAspectRatio,
} from "../utils/childrensBookAuthoringPageGeometry";

function clean(value) {
  return String(value ?? "").trim();
}

function wholeBookWriterPageLabel(
  pageStart,
  pageEnd
) {
  const start =
    Number(pageStart);

  const end =
    Number(pageEnd);

  if (
    !Number.isInteger(start) ||
    start < 1 ||
    !Number.isInteger(end) ||
    end < start
  ) {
    return "";
  }

  return end > start
    ? `Pages ${start}–${end}`
    : `Page ${start}`;
}

function wholeBookUnitCardId(
  unitId,
  absoluteIndex = 0
) {
  const identity =
    clean(unitId);

  return identity
    ? `whole-book-unit-${encodeURIComponent(
        identity
      )}`
    : `whole-book-unit-position-${
        absoluteIndex + 1
      }`;
}

function safeImageUrl(value) {
  const candidate = clean(value);

  if (!candidate) return "";

  try {
    const parsed = new URL(candidate);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function protectedAsset(asset) {
  return Boolean(
    asset &&
    (
      asset.source === "upload" ||
      asset.storageProvider === "local"
    )
  );
}

function statusStyle(ready) {
  return {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    background: ready
      ? "#dcfce7"
      : "#ffedd5",
    color: ready
      ? "#166534"
      : "#9a3412",
  };
}

const ASSEMBLY_WINDOW_SIZE = 20;

function normalizedAssemblyWindowStart(
  value
) {
  const number = Number(value);

  return (
    Number.isInteger(number) &&
    number >= 0
  )
    ? number
    : 0;
}

export default function ChildrensBookBookAssembly({
  lang = "en",
  projectPlan = {},
  persistenceEnabled = false,
  projectId = "",
  initialUnitId = "",
  initialWindowStart = 0,
  onLoadChildrensBookAssemblyRange,
  onLoadChildrensBookAssemblyUnit,
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  onLoadChildrensBookAssetFile,
  onOpenBookMap,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onOpenCoverPlan,
  onReturn,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const [
    coverExpanded,
    setCoverExpanded,
  ] = useState(false);

  const [
    activeUnitId,
    setActiveUnitId,
  ] = useState("");

  const [
    windowStart,
    setWindowStart,
  ] = useState(
    () =>
      normalizedAssemblyWindowStart(
        initialWindowStart
      )
  );

  const localUnits = useMemo(
    () =>
      Array.isArray(
        projectPlan.bookMap?.units
      )
        ? projectPlan.bookMap.units
        : [],
    [projectPlan.bookMap?.units]
  );

  const manuscriptEntries =
    useMemo(
      () =>
        Array.isArray(
          projectPlan
            .manuscriptPlan
            ?.entries
        )
          ? projectPlan
              .manuscriptPlan
              .entries
          : [],
      [
        projectPlan
          .manuscriptPlan
          ?.entries,
      ]
    );

  const manuscriptsByUnit =
    useMemo(
      () =>
        manuscriptEntries.reduce(
          (result, entry) => {
            const unitId =
              clean(entry?.unitId);

            if (unitId) {
              result[unitId] = entry;
            }

            return result;
          },
          {}
        ),
      [manuscriptEntries]
    );

  const assemblyApiEnabled =
    Boolean(
      persistenceEnabled &&
      clean(projectId) &&
      typeof onLoadChildrensBookAssemblyRange ===
        "function" &&
      typeof onLoadChildrensBookAssemblyUnit ===
        "function"
    );

  const [
    remoteAssembly,
    setRemoteAssembly,
  ] = useState(null);

  const [
    remoteAssemblyLoading,
    setRemoteAssemblyLoading,
  ] = useState(false);

  const [
    remoteAssemblyError,
    setRemoteAssemblyError,
  ] = useState("");

  const [
    remoteUnitDetails,
    setRemoteUnitDetails,
  ] = useState({});

  const [
    remoteUnitLoadingId,
    setRemoteUnitLoadingId,
  ] = useState("");

  const [
    remoteUnitErrors,
    setRemoteUnitErrors,
  ] = useState({});

  const remoteUnitRequestSequence =
    useRef(0);

  const initialReviewRestorationRef =
    useRef("");

  const toggleUnitDetailsRef =
    useRef(null);

  useEffect(() => {
    remoteUnitRequestSequence.current += 1;

    if (!assemblyApiEnabled) {
      setRemoteAssembly(null);
      setRemoteAssemblyLoading(false);
      setRemoteAssemblyError("");
      setRemoteUnitDetails({});
      setRemoteUnitLoadingId("");
      setRemoteUnitErrors({});

      return () => {};
    }

    let cancelled = false;

    setRemoteAssembly(null);
    setRemoteAssemblyLoading(true);
    setRemoteAssemblyError("");
    setActiveUnitId("");
    setRemoteUnitDetails({});
    setRemoteUnitLoadingId("");
    setRemoteUnitErrors({});

    async function loadRange() {
      try {
        const payload =
          await onLoadChildrensBookAssemblyRange(
            clean(projectId),
            {
              offset:
                Math.max(
                  0,
                  Number(
                    windowStart
                  ) || 0
                ),

              limit:
                ASSEMBLY_WINDOW_SIZE,
            }
          );

        if (cancelled) return;

        const assembly =
          payload?.assembly;

        if (
          !assembly ||
          !Array.isArray(
            assembly.units
          ) ||
          !assembly.pagination
        ) {
          throw new Error(
            isFR
              ? "Le serveur n’a pas retourné une plage d’assemblage valide."
              : "The server did not return a valid Book Assembly range."
          );
        }

        setRemoteAssembly(
          assembly
        );
      } catch (error) {
        if (!cancelled) {
          setRemoteAssemblyError(
            clean(error?.message) ||
            (
              isFR
                ? "Impossible de charger cette plage du livre."
                : "Could not load this book range."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setRemoteAssemblyLoading(
            false
          );
        }
      }
    }

    loadRange();

    return () => {
      cancelled = true;
    };
  }, [
    assemblyApiEnabled,
    isFR,
    onLoadChildrensBookAssemblyRange,
    projectId,
    windowStart,
  ]);

  const remotePagination =
    remoteAssembly?.pagination ||
    {};

  const units =
    useMemo(
      () =>
        assemblyApiEnabled
          ? (
              Array.isArray(
                remoteAssembly?.units
              )
                ? remoteAssembly.units
                : []
            )
          : localUnits,
      [
        assemblyApiEnabled,
        localUnits,
        remoteAssembly?.units,
      ]
    );

  const totalUnits =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remotePagination.total
          ) || 0
        )
      : localUnits.length;

  const activeCover =
    useMemo(
      () =>
        assemblyApiEnabled
          ? (
              remoteAssembly
                ?.cover ||
              null
            )
          : currentChildrensBookCover(
              childrensBookAssets,
              "front_cover"
            ),
      [
        assemblyApiEnabled,
        childrensBookAssets,
        remoteAssembly,
      ]
    );

  const illustrationsByUnit =
    useMemo(
      () => {
        if (assemblyApiEnabled) {
          return units.reduce(
            (result, unit) => {
              const unitId =
                clean(unit?.id);

              if (
                unitId &&
                unit?.illustration
              ) {
                result[unitId] =
                  unit.illustration;
              }

              return result;
            },
            {}
          );
        }

        return childrensBookIllustrationsByUnit(
          childrensBookAssets
        );
      },
      [
        assemblyApiEnabled,
        childrensBookAssets,
        units,
      ]
    );

  const selectedIllustration =
    useMemo(
      () =>
        assemblyApiEnabled
          ? (
              remoteUnitDetails[
                activeUnitId
              ]?.illustration ||
              illustrationsByUnit[
                activeUnitId
              ] ||
              null
            )
          : (
              illustrationsByUnit[
                activeUnitId
              ] || null
            ),
      [
        activeUnitId,
        assemblyApiEnabled,
        illustrationsByUnit,
        remoteUnitDetails,
      ]
    );

  const windowCount =
    Math.max(
      1,
      Math.ceil(
        totalUnits /
          ASSEMBLY_WINDOW_SIZE
      )
    );

  const maximumWindowStart =
    Math.max(
      0,
      (
        windowCount - 1
      ) * ASSEMBLY_WINDOW_SIZE
    );

  const reportedOffset =
    Number(
      remotePagination.offset
    );

  const normalizedWindowStart =
    assemblyApiEnabled &&
    Number.isInteger(
      reportedOffset
    )
      ? Math.max(
          0,
          reportedOffset
        )
      : Math.min(
          windowStart,
          maximumWindowStart
        );

  const visibleUnits =
    useMemo(
      () =>
        assemblyApiEnabled
          ? units
          : localUnits.slice(
              normalizedWindowStart,
              normalizedWindowStart +
                ASSEMBLY_WINDOW_SIZE
            ),
      [
        assemblyApiEnabled,
        localUnits,
        normalizedWindowStart,
        units,
      ]
    );

  const windowEnd =
    Math.min(
      totalUnits,
      normalizedWindowStart +
        visibleUnits.length
    );

  const remoteWriterPagination =
    remoteAssembly
      ?.writerPagination ||
    {};

  const localPhysicalPageItems =
    useMemo(
      () =>
        buildChildrensBookPhysicalPageItems(
          localUnits
        ),
      [localUnits]
    );

  const totalWriterPages =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteWriterPagination
              .totalPages
          ) || 0
        )
      : childrensBookTotalPhysicalPages(
          localUnits
        );

  const localFirstVisiblePageItem =
    localPhysicalPageItems[
      normalizedWindowStart
    ] || null;

  const localLastVisiblePageItem =
    windowEnd >
    normalizedWindowStart
      ? (
          localPhysicalPageItems[
            windowEnd - 1
          ] || null
        )
      : null;

  const visibleWriterPageStart =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteWriterPagination
              .firstVisiblePage
          ) || 0
        )
      : Math.max(
          0,
          Number(
            localFirstVisiblePageItem
              ?.alignmentPage ||
            localFirstVisiblePageItem
              ?.pageStart
          ) || 0
        );

  const visibleWriterPageEnd =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteWriterPagination
              .lastVisiblePage
          ) || 0
        )
      : Math.max(
          0,
          Number(
            localLastVisiblePageItem
              ?.pageEnd
          ) || 0
        );

  const writerPageMetaFor = (
    unit,
    absoluteIndex
  ) => {
    const source =
      assemblyApiEnabled
        ? unit
        : (
            localPhysicalPageItems[
              absoluteIndex
            ] || null
          );

    const rawStart =
      Number(
        source?.pageStart
      );

    const rawEnd =
      Number(
        source?.pageEnd
      );

    const rawAlignment =
      Number(
        source?.alignmentPage
      );

    const pageStart =
      Number.isInteger(
        rawStart
      ) &&
      rawStart > 0
        ? rawStart
        : 0;

    const pageEnd =
      Number.isInteger(
        rawEnd
      ) &&
      rawEnd >= pageStart &&
      pageStart > 0
        ? rawEnd
        : 0;

    const alignmentPage =
      Number.isInteger(
        rawAlignment
      ) &&
      rawAlignment > 0
        ? rawAlignment
        : null;

    return {
      pageStart,
      pageEnd,
      alignmentPage,
      label:
        wholeBookWriterPageLabel(
          pageStart,
          pageEnd
        ),
    };
  };

  const assemblyDisplayError =
    assemblyApiEnabled
      ? remoteAssemblyError
      : childrensBookAssetsError;

  const assemblyDisplayLoading =
    assemblyApiEnabled
      ? remoteAssemblyLoading
      : childrensBookAssetsLoading;

  useEffect(() => {
    if (
      !assemblyApiEnabled &&
      windowStart !==
        normalizedWindowStart
    ) {
      setWindowStart(
        normalizedWindowStart
      );
    }
  }, [
    assemblyApiEnabled,
    normalizedWindowStart,
    windowStart,
  ]);

  useEffect(() => {
    if (
      activeUnitId &&
      !visibleUnits.some(
        (unit) =>
          clean(unit?.id) ===
          activeUnitId
      )
    ) {
      setActiveUnitId("");
    }
  }, [
    activeUnitId,
    visibleUnits,
  ]);

  const previewAssets =
    useMemo(() => {
      const rows = [
        coverExpanded
          ? activeCover
          : null,
        selectedIllustration,
      ].filter(Boolean);

      const seen = new Set();

      return rows.filter((asset) => {
        const id = clean(asset?.id);

        if (!id || seen.has(id)) {
          return false;
        }

        seen.add(id);
        return true;
      });
    }, [
      activeCover,
      coverExpanded,
      selectedIllustration,
    ]);

  const [
    protectedPreviewUrls,
    setProtectedPreviewUrls,
  ] = useState({});

  const [
    protectedPreviewErrors,
    setProtectedPreviewErrors,
  ] = useState({});

  const [
    protectedPreviewsLoading,
    setProtectedPreviewsLoading,
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    const assetsToLoad =
      previewAssets.filter(
        protectedAsset
      );

    setProtectedPreviewUrls({});
    setProtectedPreviewErrors({});

    if (
      assetsToLoad.length === 0 ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setProtectedPreviewsLoading(
        false
      );

      return () => {};
    }

    setProtectedPreviewsLoading(true);

    async function loadPreviews() {
      const nextUrls = {};
      const nextErrors = {};

      for (const asset of assetsToLoad) {
        const id = clean(asset?.id);
        const url =
          clean(asset?.publicUrl);

        if (!id || !url) continue;

        try {
          const blob =
            await onLoadChildrensBookAssetFile(
              url
            );

          if (cancelled) return;

          if (
            typeof URL === "undefined" ||
            typeof URL.createObjectURL !==
              "function"
          ) {
            throw new Error(
              "Object URL unavailable"
            );
          }

          const objectUrl =
            URL.createObjectURL(blob);

          objectUrls.push(objectUrl);
          nextUrls[id] = objectUrl;
        } catch {
          nextErrors[id] = isFR
            ? "Impossible de charger cette image enregistrée."
            : "Could not load this saved image.";
        }
      }

      if (!cancelled) {
        setProtectedPreviewUrls(
          nextUrls
        );

        setProtectedPreviewErrors(
          nextErrors
        );
      }
    }

    loadPreviews().finally(() => {
      if (!cancelled) {
        setProtectedPreviewsLoading(
          false
        );
      }
    });

    return () => {
      cancelled = true;

      if (
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        objectUrls.forEach(
          (url) =>
            URL.revokeObjectURL(url)
        );
      }
    };
  }, [
    previewAssets,
    onLoadChildrensBookAssetFile,
    isFR,
  ]);

  async function toggleUnitDetails(
    requestedUnitId
  ) {
    const unitId =
      clean(
        requestedUnitId
      );

    if (!unitId) return;

    if (
      activeUnitId ===
      unitId
    ) {
      setActiveUnitId("");
      return;
    }

    if (!assemblyApiEnabled) {
      setActiveUnitId(
        unitId
      );
      return;
    }

    if (
      remoteUnitDetails[
        unitId
      ]
    ) {
      setActiveUnitId(
        unitId
      );
      return;
    }

    const requestSequence =
      remoteUnitRequestSequence.current +
      1;

    remoteUnitRequestSequence.current =
      requestSequence;

    setRemoteUnitLoadingId(
      unitId
    );

    setRemoteUnitErrors(
      (current) => ({
        ...current,
        [unitId]: "",
      })
    );

    try {
      const payload =
        await onLoadChildrensBookAssemblyUnit(
          clean(projectId),
          unitId
        );

      if (
        remoteUnitRequestSequence.current !==
        requestSequence
      ) {
        return;
      }

      const detail =
        payload?.assemblyUnit;

      if (
        !detail?.unit ||
        clean(
          detail.unit.id
        ) !== unitId
      ) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné les détails de l’unité demandée."
            : "The server did not return the requested unit details."
        );
      }

      setRemoteUnitDetails(
        (current) => ({
          ...current,
          [unitId]:
            detail,
        })
      );

      setActiveUnitId(
        unitId
      );
    } catch (error) {
      if (
        remoteUnitRequestSequence.current !==
        requestSequence
      ) {
        return;
      }

      setRemoteUnitErrors(
        (current) => ({
          ...current,
          [unitId]:
            clean(
              error?.message
            ) ||
            (
              isFR
                ? "Impossible de charger les détails de cette unité."
                : "Could not load this unit’s details."
            ),
        })
      );
    } finally {
      if (
        remoteUnitRequestSequence.current ===
        requestSequence
      ) {
        setRemoteUnitLoadingId(
          (current) =>
            current === unitId
              ? ""
              : current
        );
      }
    }
  }

  toggleUnitDetailsRef.current =
    toggleUnitDetails;

  useEffect(() => {
    const requestedUnitId =
      clean(initialUnitId);

    if (
      !requestedUnitId ||
      (
        assemblyApiEnabled &&
        remoteAssemblyLoading
      )
    ) {
      return () => {};
    }

    const visibleIndex =
      visibleUnits.findIndex(
        (unit) =>
          clean(unit?.id) ===
          requestedUnitId
      );

    if (visibleIndex < 0) {
      return () => {};
    }

    const requestedUnit =
      visibleUnits[visibleIndex];

    const reportedPosition =
      Number(
        requestedUnit?.position
      );

    const absoluteIndex =
      assemblyApiEnabled &&
      Number.isInteger(
        reportedPosition
      ) &&
      reportedPosition > 0
        ? reportedPosition - 1
        : normalizedWindowStart +
          visibleIndex;

    const restorationKey =
      `${requestedUnitId}:` +
      `${normalizedWindowStart}`;

    if (
      initialReviewRestorationRef
        .current === restorationKey
    ) {
      return () => {};
    }

    initialReviewRestorationRef.current =
      restorationKey;

    let cancelled = false;

    async function restoreReviewLocation() {
      await toggleUnitDetailsRef
        .current?.(
          requestedUnitId
        );

      if (cancelled) {
        return;
      }

      const cardId =
        wholeBookUnitCardId(
          requestedUnitId,
          absoluteIndex
        );

      const target =
        typeof document !== "undefined"
          ? document.getElementById(
              cardId
            )
          : null;

      target?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
    }

    restoreReviewLocation();

    return () => {
      cancelled = true;
    };
  }, [
    assemblyApiEnabled,
    initialUnitId,
    normalizedWindowStart,
    remoteAssemblyLoading,
    visibleUnits,
  ]);

  function previewUrlFor(asset) {
    if (!asset) return "";

    if (protectedAsset(asset)) {
      return (
        protectedPreviewUrls[
          clean(asset.id)
        ] || ""
      );
    }

    return safeImageUrl(
      asset.publicUrl
    );
  }

  const requiredIllustrationUnits =
    localUnits.filter(
      (unit) =>
        unit.illustrationPlan &&
        unit.illustrationPlan !==
          "none"
    );

  const remoteReadiness =
    remoteAssembly?.readiness ||
    {};

  const requiredIllustrationCount =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteReadiness
              .requiredIllustrationUnits
          ) || 0
        )
      : requiredIllustrationUnits
          .length;

  const unitsWithRequiredImages =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteReadiness
              .unitsWithIllustrations
          ) || 0
        )
      : requiredIllustrationUnits
          .filter(
            (unit) =>
              Boolean(
                illustrationsByUnit[
                  unit.id
                ]
              )
          ).length;

  const missingCount =
    assemblyApiEnabled
      ? Math.max(
          0,
          Number(
            remoteReadiness
              .missingItems
          ) || 0
        )
      : (
          activeCover ? 0 : 1
        ) +
        localUnits.reduce(
          (total, unit) => {
            const manuscript =
              manuscriptsByUnit[
                unit.id
              ];

            const textRequired =
              String(
                unit?.textAmount || ""
              )
                .trim()
                .toLowerCase() !==
              "none";

            const missingText =
              textRequired &&
              !clean(
                plainTextFromManuscriptEntry(
                  manuscript
                )
              );

            const requiresImage =
              unit.illustrationPlan &&
              unit.illustrationPlan !==
                "none";

            const missingImage =
              requiresImage &&
              !illustrationsByUnit[
                unit.id
              ];

            return (
              total +
              (
                missingText
                  ? 1
                  : 0
              ) +
              (
                missingImage
                  ? 1
                  : 0
              )
            );
          },
          0
        );

  const illustrationLabel = (value) =>
    ({
      none: isFR
        ? "Aucune illustration"
        : "No illustration",

      small_illustration: isFR
        ? "Petite illustration"
        : "Small illustration",

      half_page: isFR
        ? "Illustration sur une demi-page"
        : "Half-page illustration",

      full_page: isFR
        ? "Illustration pleine page"
        : "Full-page illustration",

      full_spread: isFR
        ? "Illustration sur toute la double page"
        : "Full-spread illustration",

      integrated_text: isFR
        ? "Texte intégré à l’illustration"
        : "Text integrated with illustration",
    }[value] || value);

  const assemblyIdentity =
    assemblyApiEnabled
      ? (
          remoteAssembly
            ?.identity ||
          projectPlan.identity ||
          {}
        )
      : (
          projectPlan.identity ||
          {}
        );

  const assemblyPageAspectRatio =
    childrensBookAuthoringPageAspectRatio(
      projectPlan
        ?.visualFormat
    );

  const bookTitle =
    clean(
      assemblyIdentity.title
    ) ||
    (
      isFR
        ? "Livre pour enfants sans titre"
        : "Untitled Children’s Book"
    );

  const publicAuthor =
    clean(
      assemblyIdentity.penName
    ) ||
    clean(
      assemblyIdentity.authorName
    ) ||
    (
      isFR
        ? "Auteur non renseigné"
        : "Author not provided"
    );

  return (
    <section
      aria-label={
        isFR
          ? "Assemblage du livre"
          : "Book Assembly"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            NextScenes Book Blueprint ·{" "}
            {isFR
              ? "Assemblage du livre"
              : "Book Assembly"}
          </div>

          <h2
            className="ns-section-title"
            style={{
              margin: "5px 0 8px",
            }}
          >
            {isFR
              ? "Révision complète du livre"
              : "Whole Book Review"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 840,
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? "Parcourez votre livre dans l’ordre de lecture, accédez directement à n’importe quelle page et ouvrez l’espace de travail approprié lorsqu’une correction est nécessaire."
              : "Browse your book in reading order, jump directly to any page, and open the appropriate workspace whenever a correction is needed."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onExit}
        >
          {isFR
            ? "Retour à Mes projets"
            : "Return to My Projects"}
        </button>
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding:
            "clamp(18px, 3vw, 26px)",
          marginBottom: 18,
          borderRadius: 18,
          background:
            "linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)",
          border:
            "1px solid rgba(180,83,9,0.20)",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#b45309",
          }}
        >
          {isFR
            ? "Votre livre en un seul endroit"
            : "Your whole book in one place"}
        </div>

        <h3
          className="ns-card-title"
          style={{
            margin: "7px 0 8px",
            fontSize:
              "clamp(20px, 3vw, 28px)",
          }}
        >
          {bookTitle}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            maxWidth: 850,
            lineHeight: 1.65,
          }}
        >
          {isFR
            ? "Commencez par la couverture, puis parcourez le livre dans l’ordre des pages. Ouvrez une entrée de page pour lire son texte avec son illustration."
            : "Begin with the cover, then browse the book in page order. Open a page entry to read its text beside its illustration."}
        </p>

        <div
          aria-label={
            isFR
              ? "Raccourcis de correction"
              : "Correction shortcuts"
          }
          style={{
            display: "flex",
            gap: 9,
            flexWrap: "wrap",
            marginTop: 16,
          }}
        >
          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={!activeUnitId}
            title={
              !activeUnitId
                ? (
                    isFR
                      ? "Ouvrez d’abord une unité du livre."
                      : "Open a book unit first."
                  )
                : undefined
            }
            onClick={() =>
              onOpenManuscriptPlan?.({
                unitId:
                  activeUnitId,
                windowStart:
                  normalizedWindowStart,
              })
            }
          >
            {isFR
              ? "Modifier le texte"
              : "Edit text"}
          </button>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={!activeUnitId}
            title={
              !activeUnitId
                ? (
                    isFR
                      ? "Ouvrez d’abord une unité du livre."
                      : "Open a book unit first."
                  )
                : undefined
            }
            onClick={() =>
              onOpenIllustrationPlan?.({
                unitId:
                  activeUnitId,
                windowStart:
                  normalizedWindowStart,
              })
            }
          >
            {isFR
              ? "Modifier les illustrations"
              : "Edit illustrations"}
          </button>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              onOpenCoverPlan?.({
                unitId:
                  activeUnitId,
                windowStart:
                  normalizedWindowStart,
              })
            }
          >
            {isFR
              ? "Modifier la couverture"
              : "Edit cover"}
          </button>

          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={!activeUnitId}
            title={
              !activeUnitId
                ? (
                    isFR
                      ? "Ouvrez d’abord une unité du livre."
                      : "Open a book unit first."
                  )
                : undefined
            }
            onClick={() =>
              onOpenBookMap?.({
                unitId:
                  activeUnitId,
                windowStart:
                  normalizedWindowStart,
              })
            }
          >
            {isFR
                  ? "Modifier les pages"
                  : "Edit pages"}
          </button>
        </div>
      </div>

      <div
        className="ns-card"
        style={{
          padding: 16,
          marginBottom: 18,
          borderRadius: 16,
          background: "#fff7ed",
          border:
            "1px solid rgba(234,88,12,0.25)",
        }}
      >
        <strong>
          {isFR
            ? "Aperçu de travail, pas mise en page finale"
            : "Working preview, not final layout"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin: "7px 0 0",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "Cette vue suit la numérotation des pages de votre Structure du livre actuelle. Elle ne crée pas de PDF, ne publie pas le livre et ne remplace pas la pagination finale de production."
            : "This working view follows the page numbering in your current Book Structure. It does not create a PDF, publish the book, or replace final production pagination."}
        </p>
      </div>

      <div
        className="ns-nbb-card-grid ns-nbb-summary-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 18,
        }}
      >
        {[
          [
            isFR
              ? "Pages du livre"
              : "Book pages",
            totalWriterPages > 0
              ? totalWriterPages
              : "—",
          ],
          [
            isFR
              ? "Illustrations jointes"
              : "Illustrations attached",
            `${unitsWithRequiredImages}/${requiredIllustrationCount}`,
          ],
          [
            isFR
              ? "Éléments manquants"
              : "Missing items",
            missingCount,
          ],
        ].map(([label, value]) => (
          <div
            key={label}
            className="ns-card ns-nbb-summary-card"
            style={{
              padding: 16,
              borderRadius: 14,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#64748b",
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 5,
                fontSize: 24,
                fontWeight: 900,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {assemblyDisplayError ? (
        <div
          role="alert"
          className="ns-card"
          style={{
            padding: 16,
            marginBottom: 18,
            borderRadius: 14,
            color: "#b91c1c",
          }}
        >
          {assemblyDisplayError}
        </div>
      ) : null}

      {assemblyDisplayLoading ||
      protectedPreviewsLoading ? (
        <div
          role="status"
          className="ns-card-meta"
          style={{
            marginBottom: 18,
            fontWeight: 800,
          }}
        >
          {isFR
            ? "Chargement des ressources du livre…"
            : "Loading book assets…"}
        </div>
      ) : null}

      {totalUnits > 0 ? (
        <nav
          aria-label={
            isFR
              ? "Sommaire du livre"
              : "Book Contents"
          }
          className="ns-card ns-nbb-major-panel"
          style={{
            padding:
              "clamp(16px, 2.5vw, 22px)",
            borderRadius: 18,
            marginBottom: 18,
            background: "#ffffff",
            border:
              "1px solid rgba(148,163,184,0.28)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "flex-start",
              gap: 14,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  color: "#b45309",
                  textTransform:
                    "uppercase",
                }}
              >
                {isFR
                  ? "Navigation du livre"
                  : "Book navigation"}
              </div>

              <h3
                className="ns-card-title"
                style={{
                  margin: "5px 0 4px",
                }}
              >
                {isFR
                  ? "Sommaire du livre"
                  : "Book Contents"}
              </h3>

              <p
                className="ns-card-meta"
                style={{ margin: 0 }}
              >
                {isFR
                  ? "Choisissez la couverture, une page ou une double page pour accéder directement à sa position."
                  : "Choose the cover, a page, or a spread to jump directly to its position."}
              </p>
            </div>

            <span
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                background: "#fff7ed",
                color: "#9a3412",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {isFR
                ? `${normalizedWindowStart + 1}–${windowEnd} sur ${totalUnits}`
                : `${normalizedWindowStart + 1}–${windowEnd} of ${totalUnits}`}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={() => {
                const target =
                  typeof document !== "undefined"
                    ? document.getElementById(
                        "whole-book-cover"
                      )
                    : null;

                target?.scrollIntoView?.({
                  behavior: "smooth",
                  block: "start",
                });
              }}
            >
              {isFR
                ? "Couverture"
                : "Cover"}
            </button>

            {visibleUnits.map(
              (unit, index) => {
                const reportedPosition =
                  Number(unit?.position);

                const absoluteIndex =
                  assemblyApiEnabled &&
                  Number.isInteger(
                    reportedPosition
                  ) &&
                  reportedPosition > 0
                    ? reportedPosition - 1
                    : normalizedWindowStart +
                      index;

                const pageMeta =
                  writerPageMetaFor(
                    unit,
                    absoluteIndex
                  );

                const pageLabel =
                  pageMeta.label ||
                  (
                    isFR
                      ? "Page non disponible"
                      : "Page unavailable"
                  );

                const contentsTitle =
                  clean(
                    unit?.workingTitle
                  );

                const contentsLabel =
                  contentsTitle
                    ? `${pageLabel} · ${contentsTitle}`
                    : pageLabel;

                const cardId =
                  wholeBookUnitCardId(
                    unit?.id,
                    absoluteIndex
                  );

                return (
                  <button
                    key={
                      clean(unit?.id) ||
                      cardId
                    }
                    className="ns-button ns-button-outline"
                    type="button"
                    aria-label={
                      isFR
                        ? `Aller à ${contentsLabel}`
                        : `Go to ${contentsLabel}`
                    }
                    onClick={async () => {
                      const unitId =
                        clean(
                          unit?.id
                        );

                      if (unitId) {
                        await toggleUnitDetails(
                          unitId
                        );
                      }

                      const target =
                        typeof document !==
                        "undefined"
                          ? document.getElementById(
                              cardId
                            )
                          : null;

                      target?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "start",
                      });
                    }}
                  >
                    {contentsLabel}
                  </button>
                );
              }
            )}
          </div>

          {windowCount > 1 ? (
            <div
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                  lineHeight: 1.55,
                }}
              >
                {isFR
                  ? "Le sommaire affiche la plage de pages actuelle."
                  : "The contents show the current page range."}
              </p>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {normalizedWindowStart > 0 ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    aria-label={
                      isFR
                        ? "Afficher les pages précédentes du sommaire"
                        : "Show earlier book pages"
                    }
                    onClick={() => {
                      setActiveUnitId("");

                      setWindowStart(
                        Math.max(
                          0,
                          normalizedWindowStart -
                            ASSEMBLY_WINDOW_SIZE
                        )
                      );
                    }}
                  >
                    {isFR
                      ? "Pages précédentes"
                      : "Earlier pages"}
                  </button>
                ) : null}

                {normalizedWindowStart <
                maximumWindowStart ? (
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    aria-label={
                      isFR
                        ? "Afficher les pages suivantes du sommaire"
                        : "Show next book pages"
                    }
                    onClick={() => {
                      setActiveUnitId("");

                      setWindowStart(
                        Math.min(
                          maximumWindowStart,
                          normalizedWindowStart +
                            ASSEMBLY_WINDOW_SIZE
                        )
                      );
                    }}
                  >
                    {isFR
                      ? "Plus de pages"
                      : "More pages"}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </nav>
      ) : null}

      <article
        id="whole-book-cover"
        className="ns-card ns-nbb-major-panel"
        style={{
          padding:
            "clamp(16px, 2.5vw, 22px)",
          borderRadius: 18,
          marginBottom: 18,
          border:
            activeCover
              ? "1px solid rgba(22,101,52,0.20)"
              : "1px solid rgba(185,28,28,0.22)",
          background:
            "linear-gradient(145deg, #ffffff, #fffaf5)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: "#b45309",
                textTransform:
                  "uppercase",
              }}
            >
              {isFR
                ? "Couverture avant"
                : "Front Cover"}
            </div>

            <h3
              className="ns-card-title"
              style={{
                margin: "7px 0 4px",
              }}
            >
              {bookTitle}
            </h3>

            <p
              className="ns-card-meta"
              style={{ margin: 0 }}
            >
              {publicAuthor}
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span
              style={statusStyle(
                Boolean(activeCover)
              )}
            >
              {activeCover
                ? (
                    isFR
                      ? "Couverture jointe"
                      : "Cover attached"
                  )
                : (
                    isFR
                      ? "Couverture manquante"
                      : "Cover missing"
                  )}
            </span>

            {activeCover ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                aria-expanded={
                  coverExpanded
                }
                onClick={() =>
                  setCoverExpanded(
                    (value) => !value
                  )
                }
              >
                {coverExpanded
                  ? (
                      isFR
                        ? "Masquer la couverture"
                        : "Hide cover"
                    )
                  : (
                      isFR
                        ? "Réviser la couverture"
                        : "Review cover"
                    )}
              </button>
            ) : (
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
              onOpenCoverPlan?.({
                unitId:
                  activeUnitId,
                windowStart:
                  normalizedWindowStart,
              })
            }
              >
                {isFR
                  ? "Ouvrir la couverture"
                  : "Open Cover"}
              </button>
            )}
          </div>
        </div>

        {!activeCover ? (
          <p
            className="ns-card-meta"
            style={{
              margin: "12px 0 0",
              color: "#991b1b",
            }}
          >
            {isFR
              ? "Ajoutez une couverture active avant la révision finale."
              : "Add an active cover before final review."}
          </p>
        ) : null}

        {coverExpanded &&
        activeCover ? (
          previewUrlFor(
            activeCover
          ) ? (
            <div
              style={{
                marginTop: 16,
                display: "grid",
                placeItems: "center",
                padding: 16,
                borderRadius: 16,
                background: "#f8fafc",
              }}
            >
              <img
                src={previewUrlFor(
                  activeCover
                )}
                alt={
                  isFR
                    ? `Couverture de ${bookTitle}`
                    : `${bookTitle} cover`
                }
                loading="lazy"
                style={{
                  display: "block",
                  maxWidth:
                    "min(320px, 100%)",
                  maxHeight: 420,
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            </div>
          ) : (
            <p
              role="status"
              className="ns-card-meta"
              style={{
                margin: "14px 0 0",
                color:
                  protectedPreviewsLoading
                    ? "#475569"
                    : "#b91c1c",
                fontWeight: 800,
              }}
            >
              {protectedPreviewsLoading
                ? (
                    isFR
                      ? "Chargement de la couverture…"
                      : "Loading cover…"
                  )
                : (
                    protectedPreviewErrors[
                      clean(activeCover.id)
                    ] ||
                    (
                      isFR
                        ? "L’aperçu de la couverture n’est pas disponible."
                        : "The cover preview is unavailable."
                    )
                  )}
            </p>
          )
        ) : null}
      </article>

      {assemblyApiEnabled &&
      !remoteAssembly ? null : totalUnits === 0 ? (
        <div
          className="ns-card"
          style={{
            padding: 24,
            borderRadius: 18,
            textAlign: "center",
          }}
        >
          <strong>
            {isFR
              ? "Le Plan du livre est vide"
              : "The Book Map Is Empty"}
          </strong>

          <p
            className="ns-card-meta"
            style={{
              margin: "8px auto 16px",
              maxWidth: 640,
            }}
          >
            {isFR
              ? "Ajoutez des pages au livre avant de l’assembler."
              : "Add pages before assembling the book."}
          </p>

          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={() =>
              onOpenBookMap?.({
                unitId: "",
                windowStart:
                  normalizedWindowStart,
              })
            }
          >
            {isFR
              ? "Ouvrir le Plan du livre"
              : "Open Book Map"}
          </button>
        </div>
      ) : (
        <>
          <div
            className="ns-card"
            style={{
              padding: 16,
              marginBottom: 14,
              borderRadius: 16,
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <strong>
                {
                  visibleWriterPageStart > 0 &&
                  visibleWriterPageEnd > 0 &&
                  totalWriterPages > 0
                    ? (
                        isFR
                          ? `Pages du livre ${visibleWriterPageStart}–${visibleWriterPageEnd} sur ${totalWriterPages}`
                          : `Book pages ${visibleWriterPageStart}–${visibleWriterPageEnd} of ${totalWriterPages}`
                      )
                    : (
                        isFR
                          ? "Pages du livre"
                          : "Book pages"
                      )
                }
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin: "5px 0 0",
                }}
              >
                {isFR
                  ? "Ouvrez une page pour examiner son manuscrit, son illustration et sa mise en page."
                  : "Open a page to review its manuscript, illustration, and layout."}
              </p>
            </div>

            {windowCount > 1 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    normalizedWindowStart === 0
                  }
                  onClick={() => {
                    setActiveUnitId("");

                    setWindowStart(
                      Math.max(
                        0,
                        normalizedWindowStart -
                          ASSEMBLY_WINDOW_SIZE
                      )
                    );
                  }}
                >
                  {isFR
                    ? "Pages précédentes"
                    : "Previous pages"}
                </button>

                <select
                  className="ns-input"
                  aria-label={
                    isFR
                      ? "Choisir une plage de pages du livre"
                      : "Choose book page range"
                  }
                  value={
                    normalizedWindowStart
                  }
                  onChange={(event) => {
                    setActiveUnitId("");

                    setWindowStart(
                      Number(
                        event.target.value
                      )
                    );
                  }}
                  style={{
                    width: "auto",
                    minWidth: 190,
                  }}
                >
                  {Array.from(
                    {
                      length:
                        windowCount,
                    },
                    (_, rangeIndex) => {
                      const start =
                        rangeIndex *
                        ASSEMBLY_WINDOW_SIZE;

                      const end =
                        Math.min(
                          totalUnits,
                          start +
                            ASSEMBLY_WINDOW_SIZE
                        );

                      const localRangeFirst =
                        !assemblyApiEnabled
                          ? (
                              localPhysicalPageItems[
                                start
                              ] || null
                            )
                          : null;

                      const localRangeLast =
                        !assemblyApiEnabled &&
                        end > start
                          ? (
                              localPhysicalPageItems[
                                end - 1
                              ] || null
                            )
                          : null;

                      const rangePageStart =
                        assemblyApiEnabled
                          ? (
                              start ===
                              normalizedWindowStart
                                ? visibleWriterPageStart
                                : 0
                            )
                          : Math.max(
                              0,
                              Number(
                                localRangeFirst
                                  ?.alignmentPage ||
                                localRangeFirst
                                  ?.pageStart
                              ) || 0
                            );

                      const rangePageEnd =
                        assemblyApiEnabled
                          ? (
                              start ===
                              normalizedWindowStart
                                ? visibleWriterPageEnd
                                : 0
                            )
                          : Math.max(
                              0,
                              Number(
                                localRangeLast
                                  ?.pageEnd
                              ) || 0
                            );

                      const rangePageLabel =
                        wholeBookWriterPageLabel(
                          rangePageStart,
                          rangePageEnd
                        );

                      return (
                        <option
                          key={start}
                          value={start}
                        >
                          {
                            rangePageLabel
                              ? (
                                  isFR
                                    ? `Plage ${rangeIndex + 1} · ${rangePageLabel}`
                                    : `Range ${rangeIndex + 1} · ${rangePageLabel}`
                                )
                              : (
                                  isFR
                                    ? `Plage ${rangeIndex + 1}`
                                    : `Range ${rangeIndex + 1}`
                                )
                          }
                        </option>
                      );
                    }
                  )}
                </select>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  disabled={
                    normalizedWindowStart >=
                    maximumWindowStart
                  }
                  onClick={() => {
                    setActiveUnitId("");

                    setWindowStart(
                      Math.min(
                        maximumWindowStart,
                        normalizedWindowStart +
                          ASSEMBLY_WINDOW_SIZE
                      )
                    );
                  }}
                >
                  {isFR
                    ? "Pages suivantes"
                    : "Next pages"}
                </button>
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gap: 12,
            }}
          >
            {visibleUnits.map(
              (unit, index) => {
                const reportedPosition =
                  Number(
                    unit?.position
                  );

                const absoluteIndex =
                  assemblyApiEnabled &&
                  Number.isInteger(
                    reportedPosition
                  ) &&
                  reportedPosition > 0
                    ? reportedPosition - 1
                    : normalizedWindowStart +
                      index;

                const unitId =
                  clean(unit?.id);

                const remoteDetail =
                  assemblyApiEnabled
                    ? (
                        remoteUnitDetails[
                          unitId
                        ] ||
                        null
                      )
                    : null;

                const displayUnit =
                  remoteDetail?.unit ||
                  unit;

                const manuscript =
                  assemblyApiEnabled
                    ? (
                        remoteDetail
                          ?.manuscript ||
                        {}
                      )
                    : (
                        manuscriptsByUnit[
                          unitId
                        ] ||
                        {}
                      );

                const manuscriptText =
                  clean(
                    plainTextFromManuscriptEntry(
                      manuscript
                    )
                  );

                const illustration =
                  assemblyApiEnabled
                    ? (
                        remoteDetail
                          ?.illustration ||
                        unit
                          ?.illustration ||
                        null
                      )
                    : (
                        illustrationsByUnit[
                          unitId
                        ] ||
                        null
                      );

                const requiresImage =
                  assemblyApiEnabled
                    ? Boolean(
                        unit
                          ?.requiresIllustration
                      )
                    : Boolean(
                        displayUnit
                          .illustrationPlan &&
                        displayUnit
                          .illustrationPlan !==
                            "none"
                      );

                const hasIllustration =
                  assemblyApiEnabled
                    ? Boolean(
                        unit
                          ?.hasIllustration
                      )
                    : Boolean(
                        illustration
                      );

                const textRequired =
                  assemblyApiEnabled
                    ? (
                        unit?.textRequired !==
                        false
                      )
                    : (
                        String(
                          displayUnit
                            ?.textAmount ||
                          ""
                        )
                          .trim()
                          .toLowerCase() !==
                        "none"
                      );

                const textComplete =
                  !textRequired ||
                  (
                    assemblyApiEnabled
                      ? Boolean(
                          unit?.hasText
                        )
                      : Boolean(
                          manuscriptText
                        )
                  );

                const ready =
                  assemblyApiEnabled
                    ? Boolean(
                        unit?.ready
                      )
                    : (
                        textComplete &&
                        (
                          !requiresImage ||
                          hasIllustration
                        )
                      );

                const expanded =
                  activeUnitId ===
                  unitId;

                const imageUrl =
                  expanded
                    ? previewUrlFor(
                        illustration
                      )
                    : "";

                const savedImageGeometryStyle =
                  childrensBookAuthoringSurfaceBoxStyle(
                    displayUnit
                      ?.authoringImageGeometry
                  );

                const showSavedImageGeometry =
                  Boolean(
                    savedImageGeometryStyle &&
                    assemblyPageAspectRatio
                  );

                const pageMeta =
                  writerPageMetaFor(
                    unit,
                    absoluteIndex
                  );

                const pageLabel =
                  pageMeta.label ||
                  (
                    isFR
                      ? "Page non disponible"
                      : "Page unavailable"
                  );

                const title =
                  clean(
                    displayUnit.workingTitle
                  ) ||
                  pageLabel;

                const illustrationDescription =
                  clean(
                    displayUnit.illustrationNote
                  ) ||
                  clean(
                    displayUnit.purpose
                  ) ||
                  clean(
                    illustration?.description
                  ) ||
                  clean(
                    illustration?.caption
                  ) ||
                  (
                    isFR
                      ? "Aucune description d’illustration fournie."
                      : "No illustration description provided."
                  );

                const cardId =
                  wholeBookUnitCardId(
                    unit?.id,
                    absoluteIndex
                  );

                const detailId =
                  `assembly-unit-${absoluteIndex + 1}`;

                return (
                  <article
                    id={cardId}
                    key={
                      unitId ||
                      detailId
                    }
                    className="ns-card"
                    style={{
                      padding:
                        "clamp(14px, 2vw, 18px)",
                      borderRadius: 16,
                      border: ready
                        ? "1px solid rgba(22,101,52,0.20)"
                        : "1px solid rgba(234,88,12,0.28)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "flex-start",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          minWidth: 0,
                          flex: "1 1 340px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            color: "#64748b",
                            textTransform:
                              "uppercase",
                          }}
                        >
                          {pageLabel}
                        </div>

                        <h3
                          className="ns-card-title"
                          style={{
                            margin:
                              "5px 0 4px",
                          }}
                        >
                          {title}
                        </h3>

                        {
                          pageMeta.alignmentPage
                            ? (
                                <p
                                  className="ns-card-meta"
                                  style={{
                                    margin: 0,
                                  }}
                                >
                                  {isFR
                                    ? `La page ${pageMeta.alignmentPage} est réservée comme page blanche juste avant cette plage de pages.`
                                    : `Page ${pageMeta.alignmentPage} is reserved as a blank page immediately before this page range.`}
                                </p>
                              )
                            : null
                        }
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems:
                            "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={statusStyle(
                            ready
                          )}
                        >
                          {ready
                            ? (
                                isFR
                                  ? "Prêt pour l’aperçu"
                                  : "Preview ready"
                              )
                            : (
                                isFR
                                  ? "À compléter"
                                  : "Needs attention"
                              )}
                        </span>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          disabled={
                            remoteUnitLoadingId ===
                            unitId
                          }
                          aria-expanded={
                            expanded
                          }
                          aria-controls={
                            detailId
                          }
                          onClick={() =>
                            toggleUnitDetails(
                              unitId
                            )
                          }
                        >
                          {remoteUnitLoadingId ===
                          unitId
                            ? (
                                isFR
                                  ? "Chargement des détails…"
                                  : "Loading details…"
                              )
                            : expanded
                              ? (
                                  isFR
                                    ? "Masquer les détails"
                                    : "Hide details"
                                )
                              : (
                                  isFR
                                    ? "Réviser la page"
                                    : "Review page"
                                )}
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(170px, 1fr))",
                        gap: 8,
                        marginTop: 12,
                      }}
                    >
                      {[
                        [
                          isFR
                            ? "Texte"
                            : "Text",
                          !textRequired
                            ? (
                                isFR
                                  ? "Non requis"
                                  : "Not required"
                              )
                            : textComplete
                              ? (
                                  isFR
                                    ? "Complet"
                                    : "Complete"
                                )
                              : (
                                  isFR
                                    ? "Manquant"
                                    : "Missing"
                                ),
                        ],
                        [
                          isFR
                            ? "Illustration"
                            : "Illustration",
                          !requiresImage
                            ? (
                                isFR
                                  ? "Non requise"
                                  : "Not required"
                              )
                            : hasIllustration
                              ? (
                                  isFR
                                    ? "Jointe"
                                    : "Attached"
                                )
                              : (
                                  isFR
                                    ? "Manquante"
                                    : "Missing"
                                ),
                        ],
                        [
                          isFR
                            ? "Disposition"
                            : "Layout",
                          clean(
                            displayUnit.layoutIntention
                          ) ||
                            (
                              isFR
                                ? "Non renseignée"
                                : "Not provided"
                            ),
                        ],
                      ].map(
                        ([label, value]) => (
                          <div
                            key={label}
                            style={{
                              padding:
                                "9px 11px",
                              borderRadius: 10,
                              background:
                                "#f8fafc",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 900,
                                color:
                                  "#64748b",
                              }}
                            >
                              {label}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                fontWeight: 700,
                              }}
                            >
                              {value}
                            </div>
                          </div>
                        )
                      )}
                    </div>

                    {remoteUnitErrors[
                      unitId
                    ] ? (
                      <p
                        role="alert"
                        className="ns-card-meta"
                        style={{
                          margin:
                            "12px 0 0",
                          color:
                            "#b91c1c",
                          fontWeight:
                            800,
                        }}
                      >
                        {
                          remoteUnitErrors[
                            unitId
                          ]
                        }
                      </p>
                    ) : null}

                    {expanded ? (
                      <div
                        id={detailId}
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(260px, 1fr))",
                          gap: 16,
                          marginTop: 16,
                          paddingTop: 16,
                          borderTop:
                            "1px solid rgba(148,163,184,0.28)",
                          alignItems:
                            "stretch",
                        }}
                      >
                        <div
                          className="ns-nbb-inner-panel"
                          style={{
                            minHeight: 240,
                            display: "grid",
                            placeItems:
                              "center",
                            padding: 16,
                            borderRadius: 14,
                            background:
                              "#f8fafc",
                          }}
                        >
                          {!requiresImage ? (
                            <div
                              style={{
                                textAlign:
                                  "center",
                              }}
                            >
                              <strong>
                                {isFR
                                  ? "Aucune illustration prévue"
                                  : "No illustration planned"}
                              </strong>

                              <p
                                className="ns-card-meta"
                                style={{
                                  margin:
                                    "7px 0 0",
                                }}
                              >
                                {illustrationLabel(
                                  displayUnit.illustrationPlan
                                )}
                              </p>
                            </div>
                          ) : imageUrl ? (
                            <div
                              style={{
                                width: "100%",
                                display: "grid",
                                gap: 14,
                                alignSelf: "start",
                              }}
                            >
                              {showSavedImageGeometry ? (
                                <div
                                  data-testid="book-assembly-saved-image-surface"
                                  style={{
                                    position:
                                      "relative",
                                    width:
                                      "min(100%, 320px)",
                                    margin:
                                      "0 auto",
                                    aspectRatio:
                                      String(
                                        assemblyPageAspectRatio
                                      ),
                                    overflow:
                                      "hidden",
                                    boxSizing:
                                      "border-box",
                                    border:
                                      "1px solid rgba(148,163,184,0.45)",
                                    borderRadius:
                                      10,
                                    background:
                                      "#ffffff",
                                  }}
                                >
                                  <div
                                    data-testid="book-assembly-saved-image-box"
                                    style={
                                      savedImageGeometryStyle
                                    }
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={
                                        isFR
                                          ? `Illustration de ${title}`
                                          : `${title} illustration`
                                      }
                                      loading="lazy"
                                      style={{
                                        display:
                                          "block",
                                        width:
                                          "100%",
                                        height:
                                          "100%",
                                        objectFit:
                                          "contain",
                                      }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={imageUrl}
                                  alt={
                                    isFR
                                      ? `Illustration de ${title}`
                                      : `${title} illustration`
                                  }
                                  loading="lazy"
                                  style={{
                                    display:
                                      "block",
                                    width: "100%",
                                    maxHeight:
                                      440,
                                    objectFit:
                                      "contain",
                                    borderRadius:
                                      10,
                                  }}
                                />
                              )}

                              <div
                                aria-label={
                                  isFR
                                    ? `Détails de l’illustration de ${title}`
                                    : `Illustration details for ${title}`
                                }
                                style={{
                                  padding: "13px 14px",
                                  borderRadius: 12,
                                  background: "#ffffff",
                                  border:
                                    "1px solid rgba(148,163,184,0.26)",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 900,
                                    letterSpacing:
                                      "0.08em",
                                    textTransform:
                                      "uppercase",
                                    color: "#b45309",
                                  }}
                                >
                                  {isFR
                                    ? "Illustration"
                                    : "Illustration"}
                                </div>

                                <strong
                                  style={{
                                    display: "block",
                                    marginTop: 5,
                                  }}
                                >
                                  {title}
                                </strong>

                                <p
                                  className="ns-card-meta"
                                  style={{
                                    margin: "6px 0 0",
                                    lineHeight: 1.55,
                                  }}
                                >
                                  {illustrationDescription}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                textAlign:
                                  "center",
                              }}
                            >
                              <strong
                                style={{
                                  color:
                                    illustration
                                      ? "#475569"
                                      : "#9a3412",
                                }}
                              >
                                {illustration &&
                                protectedPreviewsLoading
                                  ? (
                                      isFR
                                        ? "Chargement de l’illustration…"
                                        : "Loading illustration…"
                                    )
                                  : (
                                      isFR
                                        ? "Illustration manquante"
                                        : "Illustration missing"
                                    )}
                              </strong>

                              <p
                                className="ns-card-meta"
                                style={{
                                  margin:
                                    "7px 0 12px",
                                }}
                              >
                                {illustration
                                  ? (
                                      protectedPreviewErrors[
                                        clean(
                                          illustration.id
                                        )
                                      ] ||
                                      (
                                        protectedPreviewsLoading
                                          ? (
                                              isFR
                                                ? "Préparation de l’aperçu enregistré."
                                                : "Preparing the saved preview."
                                            )
                                          : (
                                              isFR
                                                ? "L’image enregistrée ne peut pas être affichée."
                                                : "The saved image cannot be displayed."
                                            )
                                      )
                                    )
                                  : illustrationLabel(
                                      displayUnit.illustrationPlan
                                    )}
                              </p>

                              {!illustration ? (
                                <button
                                  className="ns-button ns-button-outline"
                                  type="button"
                                  onClick={() =>
                                    onOpenIllustrationPlan?.({
                                      unitId,
                                      windowStart:
                                        normalizedWindowStart,
                                    })
                                  }
                                >
                                  {isFR
                                    ? "Ouvrir les illustrations"
                                    : "Open Illustrations"}
                                </button>
                              ) : null}
                            </div>
                          )}
                        </div>

                        <div
                          className="ns-nbb-reading-panel"
                          style={{
                            padding:
                              "clamp(18px, 3vw, 26px)",
                            borderRadius: 14,
                            background:
                              "#fffdf8",
                            border:
                              "1px solid rgba(180,83,9,0.14)",
                            display: "grid",
                            alignContent:
                              "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent:
                                "flex-end",
                              marginBottom: 14,
                            }}
                          >
                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              onClick={() =>
                                onOpenManuscriptPlan?.({
                                  unitId,
                                  windowStart:
                                    normalizedWindowStart,
                                })
                              }
                            >
                              {!textRequired
                                ? (
                                    isFR
                                      ? "Voir les détails du manuscrit"
                                      : "View manuscript details"
                                  )
                                : (
                                    isFR
                                      ? "Modifier ce manuscrit"
                                      : "Edit this manuscript"
                                  )}
                            </button>
                          </div>

                          {!textRequired ? (
                            <div>
                              <strong
                                style={{
                                  color:
                                    "#166534",
                                }}
                              >
                                {isFR
                                  ? "Aucun texte requis"
                                  : "No text required"}
                              </strong>

                              <p
                                className="ns-card-meta"
                                style={{
                                  margin:
                                    "7px 0 0",
                                  lineHeight:
                                    1.55,
                                }}
                              >
                                {isFR
                                  ? "Cette page est volontairement définie sur « Aucun texte » dans la Structure du livre."
                                  : "This page is intentionally set to “No text” in Book Structure."}
                              </p>
                            </div>
                          ) : manuscriptText ? (
                            <div
                              style={{
                                whiteSpace:
                                  "pre-wrap",
                                lineHeight: 1.8,
                                fontSize:
                                  "clamp(16px, 2vw, 19px)",
                                color:
                                  "#1f2937",
                              }}
                            >
                              {manuscriptText}
                            </div>
                          ) : (
                            <div>
                              <strong
                                style={{
                                  color:
                                    "#9a3412",
                                }}
                              >
                                {isFR
                                  ? "Texte du manuscrit manquant"
                                  : "Manuscript text missing"}
                              </strong>

                              <p
                                className="ns-card-meta"
                                style={{
                                  margin:
                                    "7px 0 12px",
                                }}
                              >
                                {isFR
                                  ? "Rédigez le texte que le jeune lecteur verra ou entendra."
                                  : "Write the words the young reader will see or hear."}
                              </p>

                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                onClick={() =>
                                  onOpenManuscriptPlan?.({
                                    unitId,
                                    windowStart:
                                      normalizedWindowStart,
                                  })
                                }
                              >
                                {isFR
                                  ? "Ouvrir le manuscrit"
                                  : "Open Manuscript"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              }
            )}
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 20,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onReturn}
        >
          {isFR
            ? "Retour au NextScenes Book Blueprint"
            : "Return to NextScenes Book Blueprint"}
        </button>
      </div>
    </section>
  );
}
